import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import {
	type AssetAncestor,
	type AssetRevision,
	type ChildAssetRow,
	type CreateChildAssetRequest,
	type UpdateAssetRequest,
	addAssetPhoto,
	createChildAsset,
	deleteAssetPhoto,
	getAssetAncestors,
	getAssetChildren,
	getAssetRevisionHistory,
	updateAsset,
} from "@/services/asset-service";
import { clearAssetCache } from "../childFunctions/fetchData";
import { auth } from "@/services/auth-service";
import {
	selfOrganizationEquipmentAreas,
	getOrganizationFormUniqueFields,
} from "@/services/organization-service";
import { uploadAssetPhoto } from "@/services/storage-service";
import { getOrganizationAssetTypes } from "@/services/utility-service";
import { getManufacturers } from "@/services/utility-service";
import {
	ApartmentOutlined,
	ApiOutlined,
	ArrowLeftOutlined,
	CalendarOutlined,
	CameraOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	EnvironmentOutlined,
	ExperimentOutlined,
	HistoryOutlined,
	InfoCircleOutlined,
	LeftOutlined,
	RightOutlined,
	SaveOutlined,
	SettingOutlined,
	SwapOutlined,
	TagOutlined,
	ToolOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import MoveAssetModal from "./MoveAssetModal";
import { useUserStore } from "@/stores";
import { UserAccessLevel } from "@/stores/user-store";
import {
	Alert,
	Button,
	Card,
	Col,
	DatePicker,
	Descriptions,
	Image,
	Input,
	InputNumber,
	Modal,
	Progress,
	Row,
	Select,
	Space,
	Spin,
	Table,
	Tag,
	Timeline,
	Typography,
	Upload,
	message,
} from "antd";
import type { RcFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import React from "react";
import { useEffect, useRef, useState } from "react";

const { Text, Title } = Typography;

// ===================================
// INTEGRATION BRANDING (matches Integrations page)
// ===================================

const ASSET_INTEGRATION_BRANDING: Record<number, { abbr: string; color: string; tier: string; category: string }> = {
	// CMMS Platforms
	1: { abbr: "SC", color: "#0066CC", tier: "Tier 1", category: "CMMS" },
	2: { abbr: "ET", color: "#2DB84B", tier: "Tier 1", category: "CMMS" },
	4: { abbr: "LMB", color: "#00A651", tier: "Tier 1", category: "CMMS" },
	5: { abbr: "MX", color: "#4A90D9", tier: "Tier 1", category: "CMMS" },
	6: { abbr: "UK", color: "#FF6B35", tier: "Tier 1", category: "CMMS" },
	// IoT / Sensors
	7: { abbr: "TA", color: "#E53935", tier: "IoT", category: "IoT Sensor" },
	8: { abbr: "SAM", color: "#1A1A2E", tier: "IoT", category: "IoT Sensor" },
	9: { abbr: "MON", color: "#0288D1", tier: "IoT", category: "IoT Sensor" },
};

// ===================================
// INTERFACES
// ===================================

interface AdminAssetDetailProps {
	assetId: string;
	onBack: () => void;
	onNavigateToAsset?: (assetId: string) => void;
}

interface AssetAggregate {
	id: number;
	organizationId: number;
	locationId: number;
	organizationAssetTypeId: number;
	submittingUserId: number;
	formId: number;
	submissionId: number;
	dateOfBirth?: string;
	isDobEstimate?: boolean;
	tempAlertTag?: string;
	isTempAlertNotFound?: boolean;
	generalNotes?: string;
	isManufacturerNotFound?: boolean;
	isManufacturerPlateNotFound?: boolean;
	manufacturerDate?: string;
	isManufacturerDateNotFound?: boolean;
	modelId?: number;
	manufacturerId?: number; // Direct reference to manufacturer (independent of model)
	modelNumber?: string;
	isModelNotFound?: boolean;
	serialNumber?: string;
	isSerialNumberNotFound?: boolean;
	condition?: string;
	assetTag?: string;
	isRefrigeration?: boolean;
	isElectrical?: boolean;
	isDeleted?: string;
	isDecommissioned?: string;
	decommissionedReason?: string;
	lastAudit?: string;
	deletedBy?: number;
	powerSource?: string;
	organizationEquipmentAreaId?: number;
	organizationEquipmentSubAreaId?: number;
	uniqueFields?: any;
	externalFirebaseId?: string;
	source?: "VISION_CAPTURE" | "FORM_SUBMISSION" | "IMPORT" | "CHILD";
	parentAssetId?: number | null;
	tagType?: "PHYSICAL" | "VIRTUAL";
	parent?: {
		id: number;
		assetTag: string | null;
		tagType: "PHYSICAL" | "VIRTUAL";
		organizationAssetTypeName: string | null;
	} | null;
	childCount?: number;
	submittingUserName?: string;
	submittingUserEmail?: string;
	createdTimestamp: string;
	qaFlag?: boolean;
	updatedTimestamp: string;
	aiDeepDive?: Record<string, { text: string; confidence: number }> | null;
	electricalDetails?: {
		amps?: number;
		voltage?: number;
		wattage?: number;
		isAmpsNotFound?: boolean;
		isVoltageNotFound?: boolean;
		isWattageNotFound?: boolean;
	};
	healthScore?: {
		healthScore?: number;
		replacementYear?: number;
		lastUpdate: string;
		remainingUsefulLife?: number;
	};
	refrigerationDetails?: {
		refrigerantType?: string;
		isRefrigerantNotFound?: boolean;
		refrigerantCharge?: number;
		refrigerantChargeMeasurementUnit?: string;
		isChargeNotFound?: boolean;
		usage?: string;
	};
	warranty?: {
		hasWarranty?: boolean;
		isWarrantyExpired?: boolean;
		isNotFound?: boolean;
		startDate?: string;
		expirationDate?: string;
		type?: string;
		term?: number;
		provider?: string;
		scProviderId?: number;
	};
	assetType?: {
		id: number;
		name: string;
	};
	assetTypeCategory?: {
		id: number;
		name: string;
	};
	equipmentArea?: {
		id: number;
		name: string;
	};
	equipmentSubArea?: {
		id: number;
		name: string;
	};
	location?: {
		id: number;
		name: string;
		address1?: string;
		address2?: string;
		city?: string;
		state?: string;
		zip?: string;
		country?: string;
	};
	manufacturer?: {
		id: number;
		name: string;
		isVerified: boolean;
		timeAdded: string;
	};
	model?: {
		id: number;
		manufacturerId: number;
		modelNumber: string;
		extra?: any;
		timeAdded: string;
	};
	photos: Array<{
		id: number;
		assetId: number;
		type: string;
		url: string;
	}>;
	generativeAi: Array<any>;
	integrationSyncs?: Array<{
		orgIntegrationId: number;
		integrationId: number;
		integrationName: string;
		syncStatus: string;
		lastSyncedAt?: string;
		syncDetails?: any;
	}>;
	photoSync?: {
		id: number;
		photoSyncJobId: number;
		assetId: number;
		syncStatus: string;
		photosUploaded: number;
		photosSkipped: number;
		photosFailed: number;
		lastSyncedAt?: string;
		syncDetails?: any;
		createdAt: string;
		updatedAt: string;
	};
	nychaElevator?: {
		elevator_id: number;
		elevator_bank_name: string;
		elevator_number: string;
		elevator_type: string;
		elevator_sub_type?: string;
		building_name?: string;
		wing_section?: string;
		floor?: string;
		oem?: string;
		elevator_model?: string;
		capacity?: number;
		maximo_asset_id?: string;
		maximo_status?: string;
		rems_asset_id?: string;
		rems_location_id?: string;
		last_periodic_inspection?: string;
		last_cat1_test?: string;
		last_cat5_test?: string;
		cab_photo_url?: string;
		active_alerts_count: number;
		open_violations_count: number;
	};
}

// ===================================
// COMPONENT
// ===================================

const AdminAssetDetail = ({
	assetId,
	onBack,
	onNavigateToAsset,
}: AdminAssetDetailProps) => {
	// ===================================
	// STATE
	// ===================================

	const user = useUserStore((state) => state.user);
	const isAdmin = (user?.role?.access_level ?? user?.role?.accessLevel ?? 0) >= UserAccessLevel.ADMIN;

	const [loading, setLoading] = useState(true);
	const [asset, setAsset] = useState<AssetAggregate | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [formValues, setFormValues] = useState<UpdateAssetRequest>({});
	const [saving, setSaving] = useState(false);

	// Dropdown options state
	const [assetTypes, setAssetTypes] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [equipmentAreas, setEquipmentAreas] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [manufacturers, setManufacturers] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [loadingOptions, setLoadingOptions] = useState(false);

	// Revision history state
	const [revisions, setRevisions] = useState<AssetRevision[]>([]);
	const [loadingRevisions, setLoadingRevisions] = useState(false);
	const [showRevisionHistory, setShowRevisionHistory] = useState(false);

	// Refs to maintain focus on text inputs
	const inputRefs = useRef<Record<string, any>>({});
	const focusedInputRef = useRef<string | null>(null);
	const cursorPositionRef = useRef<number>(0);

	// Photo management state
	const [uploadingPhoto, setUploadingPhoto] = useState(false);
	const [photoType, setPhotoType] = useState<string>("OVERVIEW");

	// Org-level unique field definitions (for proper field rendering in edit mode)
	const [orgUniqueFieldDefs, setOrgUniqueFieldDefs] = useState<
		Array<{
			id: string;
			name: string;
			label: string;
			type: string;
			required?: boolean;
			placeholder?: string;
			options?: string[];
		}>
	>([]);

	// Duplicate error state
	const [duplicateError, setDuplicateError] = useState<{
		assetId: number;
		assetTag: string;
		locationInfo: string;
	} | null>(null);

	// Move Asset modal state
	const [showMoveModal, setShowMoveModal] = useState(false);

	// Hero photo carousel index
	const [heroPhotoIndex, setHeroPhotoIndex] = useState(0);

	// Child assets state
	const [childAssets, setChildAssets] = useState<ChildAssetRow[]>([]);
	const [loadingChildren, setLoadingChildren] = useState(false);
	const [ancestors, setAncestors] = useState<AssetAncestor[]>([]);
	const [showAddChildModal, setShowAddChildModal] = useState(false);
	const [addingChild, setAddingChild] = useState(false);
	const [childForm, setChildForm] = useState<CreateChildAssetRequest>({
		organizationAssetTypeId: 0,
	});

	// ===================================
	// EFFECTS
	// ===================================

	useEffect(() => {
		fetchAssetDetail();
		if (isEditMode) {
			fetchDropdownOptions();
		}
	}, [assetId, isEditMode]);

	useEffect(() => {
		getOrganizationFormUniqueFields(3)
			.then((res) => {
				if (Array.isArray(res)) {
					setOrgUniqueFieldDefs(res);
				}
			})
			.catch(() => {});
	}, []);

	// Clear error and exit edit mode when asset changes
	useEffect(() => {
		setDuplicateError(null);
		setIsEditMode(false);
		setFormValues({});
		setHeroPhotoIndex(0);
	}, [assetId]);

	// Restore focus after formValues changes for text inputs
	useEffect(() => {
		if (focusedInputRef.current && inputRefs.current[focusedInputRef.current]) {
			const inputRef = inputRefs.current[focusedInputRef.current];
			inputRef.focus();

			// Handle both Input and TextArea components
			const textElement =
				inputRef.resizableTextArea?.textArea || inputRef.input;
			if (textElement) {
				const position = cursorPositionRef.current;
				textElement.setSelectionRange(position, position);
			}
		}
	}, [formValues]);

	// ===================================
	// API FUNCTIONS
	// ===================================

	const fetchDropdownOptions = async () => {
		try {
			setLoadingOptions(true);
			// Fetch all asset types including deprecated for asset details/editing
			const [assetTypesData, equipmentAreasData, manufacturersData] =
				await Promise.all([
					getOrganizationAssetTypes(true),
					selfOrganizationEquipmentAreas(),
					getManufacturers(),
				]);

			setAssetTypes(assetTypesData);
			setEquipmentAreas(equipmentAreasData?.data || equipmentAreasData || []);
			setManufacturers(manufacturersData);
		} catch (error) {
			console.error("Failed to fetch dropdown options:", error);
			message.error("Failed to load dropdown options");
		} finally {
			setLoadingOptions(false);
		}
	};

	const fetchRevisionHistory = async () => {
		try {
			setLoadingRevisions(true);
			const history = await getAssetRevisionHistory(assetId);
			setRevisions(history);
		} catch (error) {
			console.error("Failed to fetch revision history:", error);
			message.error("Failed to load revision history");
		} finally {
			setLoadingRevisions(false);
		}
	};

	const fetchAssetDetail = async () => {
		try {
			setLoading(true);

			// Use the new asset aggregate endpoint
			const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${assetId}`;

			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch asset details (${response.status})`);
			}

			const result = await response.json();

			// Handle the response - extract data from success wrapper
			const assetData = result.success ? result.data : result;

			setAsset(assetData);
		} catch (error) {
			console.error("Failed to fetch asset details:", error);
			message.error("Failed to load asset details. Please try again.");
			setAsset(null);
		} finally {
			setLoading(false);
		}
	};

	// ===================================
	// CHILD ASSET FUNCTIONS
	// ===================================

	const fetchHierarchy = async () => {
		try {
			setLoadingChildren(true);
			const [children, chain] = await Promise.all([
				getAssetChildren(assetId),
				getAssetAncestors(assetId),
			]);
			setChildAssets(children);
			setAncestors(chain);
		} catch (error) {
			console.error("Failed to load asset hierarchy:", error);
		} finally {
			setLoadingChildren(false);
		}
	};

	// Load hierarchy info (children + ancestor breadcrumb) alongside the asset
	useEffect(() => {
		setChildAssets([]);
		setAncestors([]);
		fetchHierarchy();
	}, [assetId]);

	const openAddChildModal = () => {
		// Dropdown options are normally only loaded in edit mode
		if (assetTypes.length === 0) {
			fetchDropdownOptions();
		}
		setChildForm({ organizationAssetTypeId: 0 });
		setShowAddChildModal(true);
	};

	const handleAddChild = async () => {
		if (!childForm.organizationAssetTypeId) {
			message.error("Please select an asset type for the child asset");
			return;
		}
		try {
			setAddingChild(true);
			const created = await createChildAsset(assetId, childForm);
			message.success(
				`Child asset ${created?.assetTag ? `"${created.assetTag}" ` : ""}created`,
			);
			setShowAddChildModal(false);
			setChildForm({ organizationAssetTypeId: 0 });
			// The assets table caches the org-wide list; invalidate so the new
			// child shows up there too
			clearAssetCache();
			fetchHierarchy();
		} catch (error: any) {
			message.error(error?.message || "Failed to create child asset");
		} finally {
			setAddingChild(false);
		}
	};

	// ===================================
	// EDIT MODE FUNCTIONS
	// ===================================

	const handleEditClick = () => {
		// Initialize form values with current asset data
		// Ensure text fields are always strings to prevent controlled/uncontrolled switching
		if (asset) {
			const baseValues: any = {
				assetTag: asset.assetTag ?? "",
				organizationAssetTypeId: asset.organizationAssetTypeId,
				condition: asset.condition ?? "",
				dateOfBirth: asset.dateOfBirth,
				isDobEstimate: asset.isDobEstimate,
				generalNotes: asset.generalNotes ?? "",
				manufacturerId: asset.manufacturer?.id,
				modelNumber: asset.modelNumber ?? "",
				serialNumber: asset.serialNumber ?? "",
				manufacturerDate: asset.manufacturerDate,
				tempAlertTag: asset.tempAlertTag ?? "",
				powerSource: asset.powerSource ?? "",
				organizationEquipmentAreaId: asset.organizationEquipmentAreaId,
			};

			// Initialize unique fields from asset data + org field definitions
			const existingUniqueFields = parseUniqueFields(asset.uniqueFields) || {};
			const mergedUniqueFields: Record<string, any> = { ...existingUniqueFields };
			for (const fieldDef of orgUniqueFieldDefs) {
				if (!(fieldDef.name in mergedUniqueFields)) {
					mergedUniqueFields[fieldDef.name] = "";
				}
			}
			if (Object.keys(mergedUniqueFields).length > 0) {
				baseValues.uniqueFields = mergedUniqueFields;
			}

			// Add NYCHA elevator fields if present
			if (asset.nychaElevator) {
				baseValues.nychaElevatorBankName =
					asset.nychaElevator.elevator_bank_name ?? "";
				baseValues.nychaElevatorNumber =
					asset.nychaElevator.elevator_number ?? "";
				baseValues.nychaElevatorType = asset.nychaElevator.elevator_type;
				baseValues.nychaElevatorSubType = asset.nychaElevator.elevator_sub_type;
				baseValues.nychaElevatorBuilding =
					asset.nychaElevator.building_name ?? "";
				baseValues.nychaElevatorWingSection =
					asset.nychaElevator.wing_section ?? "";
				baseValues.nychaElevatorFloor = asset.nychaElevator.floor ?? "";
				baseValues.nychaElevatorCapacity = asset.nychaElevator.capacity;
				baseValues.nychaMaximoAssetId =
					asset.nychaElevator.maximo_asset_id ?? "";
				baseValues.nychaRemsAssetId = asset.nychaElevator.rems_asset_id ?? "";
				baseValues.nychaRemsLocationId =
					asset.nychaElevator.rems_location_id ?? "";
				baseValues.nychaLastPeriodicInspection =
					asset.nychaElevator.last_periodic_inspection;
				baseValues.nychaLastCat1Test = asset.nychaElevator.last_cat1_test;
				baseValues.nychaLastCat5Test = asset.nychaElevator.last_cat5_test;
			}

			setFormValues(baseValues);
		}
		setIsEditMode(true);
		fetchDropdownOptions();
	};

	const handleCancelEdit = () => {
		setIsEditMode(false);
		setFormValues({});
		setDuplicateError(null);
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			setDuplicateError(null); // Clear any previous errors

			// Separate NYCHA fields from regular asset fields
			const nychaFields: any = {};
			const assetFields: any = {};

			for (const [key, value] of Object.entries(formValues)) {
				if (key.startsWith("nycha")) {
					nychaFields[key] = value;
				} else {
					assetFields[key] = value;
				}
			}

			// Merge uniqueFields if they were edited
			const updatePayload: UpdateAssetRequest = { ...assetFields };
			if (assetFields.uniqueFields !== undefined) {
				// Merge with existing uniqueFields
				const currentUniqueFields =
					parseUniqueFields(asset?.uniqueFields) || {};
				const mergedUniqueFields = {
					...currentUniqueFields,
					...assetFields.uniqueFields,
				};
				updatePayload.uniqueFields = mergedUniqueFields;
			}

			// Update regular asset fields
			const updatedAsset = await updateAsset(assetId, updatePayload);

			// Update NYCHA elevator fields if any were changed
			if (Object.keys(nychaFields).length > 0 && asset?.nychaElevator) {
				const nychaPayload: any = {};

				// Only include fields that have values
				if (nychaFields.nychaElevatorBankName !== undefined)
					nychaPayload.elevatorBankName = nychaFields.nychaElevatorBankName;
				if (nychaFields.nychaElevatorNumber !== undefined)
					nychaPayload.elevatorNumber = nychaFields.nychaElevatorNumber;
				if (nychaFields.nychaElevatorType !== undefined)
					nychaPayload.elevatorType = nychaFields.nychaElevatorType;
				if (nychaFields.nychaElevatorSubType !== undefined)
					nychaPayload.elevatorSubType = nychaFields.nychaElevatorSubType;
				if (nychaFields.nychaElevatorBuilding !== undefined)
					nychaPayload.buildingName = nychaFields.nychaElevatorBuilding;
				if (nychaFields.nychaElevatorWingSection !== undefined)
					nychaPayload.wingSection = nychaFields.nychaElevatorWingSection;
				if (nychaFields.nychaElevatorFloor !== undefined)
					nychaPayload.floor = nychaFields.nychaElevatorFloor;
				if (nychaFields.nychaElevatorCapacity !== undefined)
					nychaPayload.capacity = nychaFields.nychaElevatorCapacity;
				if (nychaFields.nychaMaximoAssetId !== undefined)
					nychaPayload.maximoAssetId = nychaFields.nychaMaximoAssetId;
				if (nychaFields.nychaRemsAssetId !== undefined)
					nychaPayload.remsAssetId = nychaFields.nychaRemsAssetId;
				if (nychaFields.nychaRemsLocationId !== undefined)
					nychaPayload.remsLocationId = nychaFields.nychaRemsLocationId;
				if (nychaFields.nychaLastPeriodicInspection !== undefined)
					nychaPayload.lastPeriodicInspection =
						nychaFields.nychaLastPeriodicInspection;
				if (nychaFields.nychaLastCat1Test !== undefined)
					nychaPayload.lastCat1Test = nychaFields.nychaLastCat1Test;
				if (nychaFields.nychaLastCat5Test !== undefined)
					nychaPayload.lastCat5Test = nychaFields.nychaLastCat5Test;

				// Call NYCHA update endpoint
				const response = await fetch(
					`${VITE_CORE_API_URL}${CORE_URL}/nycha/elevator/asset/${assetId}`,
					{
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
						},
						body: JSON.stringify(nychaPayload),
					},
				);

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(
						errorData.message || "Failed to update NYCHA elevator data",
					);
				}
			}

			// Refresh asset data
			await fetchAssetDetail();

			setIsEditMode(false);
			setFormValues({});
			message.success("Asset updated successfully");

			// Refresh revision history if it's being shown
			if (showRevisionHistory) {
				await fetchRevisionHistory();
			}
		} catch (error) {
			console.error("Failed to update asset:", error);

			// Parse duplicate asset tag error
			const errorMessage =
				error instanceof Error ? error.message : "Failed to update asset";

			if (errorMessage.startsWith("DUPLICATE_ASSET_TAG||")) {
				// Parse structured error: DUPLICATE_ASSET_TAG||assetId||assetTag||locationInfo
				const parts = errorMessage.split("||");
				if (parts.length === 4) {
					setDuplicateError({
						assetId: parseInt(parts[1]),
						assetTag: parts[2],
						locationInfo: parts[3],
					});
					window.scrollTo({ top: 0, behavior: "smooth" });
				} else {
					message.error("Asset tag already exists");
				}
			} else {
				message.error(errorMessage);
			}
		} finally {
			setSaving(false);
		}
	};

	const handleToggleRevisionHistory = async () => {
		if (!showRevisionHistory) {
			await fetchRevisionHistory();
		}
		setShowRevisionHistory(!showRevisionHistory);
	};

	const handlePhotoUpload = async (file: RcFile): Promise<boolean> => {
		if (!asset) return false;

		try {
			setUploadingPhoto(true);

			// Upload file to Firebase Storage
			const photoUrl = await uploadAssetPhoto(file, assetId);

			// Add photo record to database
			const newPhoto = await addAssetPhoto(assetId, photoUrl, photoType);

			// Update local asset state with new photo
			setAsset((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					photos: [...prev.photos, newPhoto],
				};
			});

			message.success("Photo uploaded successfully");
			return true;
		} catch (error) {
			console.error("Failed to upload photo:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to upload photo",
			);
			return false;
		} finally {
			setUploadingPhoto(false);
		}
	};

	const handlePhotoDelete = async (photoId: number) => {
		if (!asset) return;

		Modal.confirm({
			title: "Delete Photo",
			content: "Are you sure you want to delete this photo?",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteAssetPhoto(assetId, photoId);

					// Update local asset state to remove photo
					setAsset((prev) => {
						if (!prev) return prev;
						return {
							...prev,
							photos: prev.photos.filter((p) => p.id !== photoId),
						};
					});

					message.success("Photo deleted successfully");
				} catch (error) {
					console.error("Failed to delete photo:", error);
					message.error(
						error instanceof Error ? error.message : "Failed to delete photo",
					);
				}
			},
		});
	};

	// ===================================
	// UTILITY FUNCTIONS
	// ===================================

	const formatCurrency = (value?: number) => {
		if (!value) return "Not available";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return "Not available";
		return dayjs(dateString).format("MM/DD/YYYY");
	};

	const getConditionColor = (condition?: string) => {
		if (!condition) return "default";
		switch (condition.toLowerCase()) {
			case "excellent":
				return "green";
			case "good":
				return "blue";
			case "fair":
				return "orange";
			case "poor":
				return "red";
			case "critical":
				return "volcano";
			default:
				return "default";
		}
	};

	const parseUniqueFields = (uniqueFields?: any) => {
		if (!uniqueFields) return null;

		try {
			// Handle if uniqueFields is already a parsed object
			let parsed = uniqueFields;
			if (typeof uniqueFields === "string") {
				parsed = JSON.parse(uniqueFields);
			}

			return parsed;
		} catch (error) {
			console.warn("Failed to parse unique fields:", error);
			return null;
		}
	};

	const getHealthScoreColor = (score?: number) => {
		if (!score) return "#d9d9d9";
		if (score > 60) return "#52c41a";
		if (score > 40) return "#faad14";
		return "#ff4d4f";
	};

	const calculateRemainingUsefulLife = () => {
		if (asset?.healthScore?.replacementYear) {
			const currentYear = new Date().getFullYear();
			const replacementYear = asset.healthScore.replacementYear;
			const remaining = replacementYear - currentYear;
			return remaining > 0 ? remaining : 0;
		}
		return asset?.healthScore?.remainingUsefulLife || null;
	};

	// ===================================
	// HELPERS
	// ===================================

	/**
	 * Selects the primary photo for the hero section.
	 * Prefers OVERVIEW type, then EQUIPMENT, WIDE_ANGLE, then falls back to the first available.
	 */
	const getPrimaryPhoto = () => {
		if (!asset?.photos || asset.photos.length === 0) return null;
		const preferred = ["OVERVIEW", "EQUIPMENT", "WIDE_ANGLE"];
		for (const type of preferred) {
			const match = asset.photos.find((p) => p.type === type);
			if (match) return match;
		}
		return asset.photos[0];
	};

	// ===================================
	// RENDER: HERO PROFILE SECTION
	// ===================================

	const renderHeroSection = () => {
		const primaryPhoto = getPrimaryPhoto();
		const photos = asset!.photos || [];
		const healthScore = asset!.healthScore?.healthScore || 0;
		const remainingLife = calculateRemainingUsefulLife();
		const replacementYear = asset!.healthScore?.replacementYear;

		const locationDisplay = asset!.location
			? `${(asset!.location as any).storeNumber || (asset!.location as any).store_number || asset!.location.name || ""} - ${asset!.location.address1 || ""}${asset!.location.city ? `, ${asset!.location.city}` : ""}${asset!.location.state ? ` ${asset!.location.state}` : ""}`
			: null;

		// Order photos: primary first, then the rest
		const orderedPhotos = primaryPhoto
			? [primaryPhoto, ...photos.filter((p) => p.id !== primaryPhoto.id)]
			: photos;

		return (
			<Card
				style={{
					marginBottom: 20,
					borderRadius: 12,
					overflow: "hidden",
					boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
				}}
				bodyStyle={{ padding: 0 }}
			>
				{/* ---- Action Buttons Row (top-right) ---- */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						padding: "12px 16px 0 16px",
						background: "#fafafa",
						borderBottom: "1px solid #f0f0f0",
					}}
				>
					<Space style={{ marginBottom: 10 }}>
						{!isEditMode ? (
							<>
								{!asset!.isDeleted && (
									<Button
										icon={<ApartmentOutlined />}
										onClick={openAddChildModal}
										size="small"
									>
										Add Child
									</Button>
								)}
								{isAdmin && !asset!.parentAssetId && (
									<Button
										icon={<SwapOutlined />}
										onClick={() => setShowMoveModal(true)}
										size="small"
									>
										Move
									</Button>
								)}
								<Button
									icon={<HistoryOutlined />}
									onClick={handleToggleRevisionHistory}
									type={showRevisionHistory ? "primary" : "default"}
									size="small"
								>
									{showRevisionHistory ? "Hide" : "Show"} History
								</Button>
								<Button
									icon={<EditOutlined />}
									onClick={handleEditClick}
									type="primary"
									size="small"
								>
									Edit
								</Button>
							</>
						) : (
							<>
								<Button
									icon={<CloseOutlined />}
									onClick={handleCancelEdit}
									disabled={saving}
									size="small"
								>
									Cancel
								</Button>
								<Button
									icon={<SaveOutlined />}
									onClick={handleSave}
									type="primary"
									loading={saving}
									size="small"
								>
									Save
								</Button>
							</>
						)}
						<Button
							icon={<ArrowLeftOutlined />}
							onClick={onBack}
							size="small"
						>
							Back
						</Button>
					</Space>
				</div>

				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						minHeight: 240,
					}}
				>
					{/* ---- Photo Area with Arrow Carousel ---- */}
					<div
						style={{
							width: 300,
							flexShrink: 0,
							background: "#1a1a1a",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							borderRight: "1px solid #f0f0f0",
							position: "relative",
							minHeight: 260,
						}}
					>
						{orderedPhotos.length > 0 ? (
							<>
								<Image.PreviewGroup>
									<Image
										src={heroPhotoIndex < orderedPhotos.length ? orderedPhotos[heroPhotoIndex].url : orderedPhotos[0].url}
										alt="Asset photo"
										style={{
											width: 300,
											height: 260,
											objectFit: "cover",
											display: "block",
										}}
										preview={{
											mask: (
												<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
													<CameraOutlined style={{ fontSize: 20 }} />
													<span style={{ fontSize: 12 }}>Click to zoom</span>
												</div>
											),
										}}
									/>
								</Image.PreviewGroup>

								{/* Photo type label */}
								<div
									style={{
										position: "absolute",
										top: 8,
										left: 8,
										background: "rgba(0,0,0,0.55)",
										color: "#fff",
										padding: "2px 10px",
										borderRadius: 4,
										fontSize: 11,
										letterSpacing: 0.3,
										textTransform: "capitalize",
									}}
								>
									{(heroPhotoIndex < orderedPhotos.length
										? orderedPhotos[heroPhotoIndex].type
										: orderedPhotos[0].type
									).replace(/_/g, " ").toLowerCase()}
								</div>

								{/* Prev / Next arrows */}
								{orderedPhotos.length > 1 && (
									<>
										<div
											onClick={(e) => {
												e.stopPropagation();
												setHeroPhotoIndex((prev) =>
													prev === 0 ? orderedPhotos.length - 1 : prev - 1,
												);
											}}
											style={{
												position: "absolute",
												left: 6,
												top: "50%",
												transform: "translateY(-50%)",
												width: 28,
												height: 28,
												borderRadius: "50%",
												background: "rgba(0,0,0,0.45)",
												color: "#fff",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												cursor: "pointer",
												fontSize: 13,
												transition: "background 0.2s",
											}}
											onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.75)")}
											onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.45)")}
										>
											<LeftOutlined />
										</div>
										<div
											onClick={(e) => {
												e.stopPropagation();
												setHeroPhotoIndex((prev) =>
													prev === orderedPhotos.length - 1 ? 0 : prev + 1,
												);
											}}
											style={{
												position: "absolute",
												right: 6,
												top: "50%",
												transform: "translateY(-50%)",
												width: 28,
												height: 28,
												borderRadius: "50%",
												background: "rgba(0,0,0,0.45)",
												color: "#fff",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												cursor: "pointer",
												fontSize: 13,
												transition: "background 0.2s",
											}}
											onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.75)")}
											onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.45)")}
										>
											<RightOutlined />
										</div>

										{/* Dot indicators */}
										<div
											style={{
												position: "absolute",
												bottom: 10,
												left: "50%",
												transform: "translateX(-50%)",
												display: "flex",
												gap: 6,
											}}
										>
											{orderedPhotos.map((_, idx) => (
												<div
													key={idx}
													onClick={(e) => {
														e.stopPropagation();
														setHeroPhotoIndex(idx);
													}}
													style={{
														width: idx === heroPhotoIndex ? 16 : 7,
														height: 7,
														borderRadius: idx === heroPhotoIndex ? 4 : "50%",
														background: idx === heroPhotoIndex ? "#fff" : "rgba(255,255,255,0.5)",
														cursor: "pointer",
														transition: "all 0.25s ease",
													}}
												/>
											))}
										</div>
									</>
								)}
							</>
						) : (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									gap: 8,
									color: "#666",
								}}
							>
								<CameraOutlined style={{ fontSize: 48, color: "#444" }} />
								<Text style={{ fontSize: 13, color: "#666" }}>
									No photos available
								</Text>
							</div>
						)}
					</div>

					{/* ---- Info Area ---- */}
					<div
						style={{
							flex: 1,
							padding: "20px 28px",
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							minWidth: 0,
						}}
					>
						{/* Top: Identity */}
						<div>
							{/* Parent breadcrumb for child assets */}
							{ancestors.length > 0 && (
								<div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>
									<ApartmentOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
									{ancestors.map((ancestor) => (
										<React.Fragment key={ancestor.id}>
											<a
												onClick={(e) => {
													e.preventDefault();
													onNavigateToAsset?.(ancestor.id.toString());
												}}
												style={{ fontSize: 12, cursor: "pointer" }}
											>
												{ancestor.assetTag || `Asset #${ancestor.id}`}
											</a>
											<Text type="secondary" style={{ fontSize: 12 }}>/</Text>
										</React.Fragment>
									))}
									<Text type="secondary" style={{ fontSize: 12 }}>
										{asset!.assetTag || `Asset #${asset!.id}`}
									</Text>
								</div>
							)}
							<div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
								<Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
									{asset!.assetTag || `Asset #${asset!.id}`}
								</Title>
								{asset!.tagType === "VIRTUAL" && (
									<Tag color="purple">Virtual Tag</Tag>
								)}
								{asset!.isDeleted && (
									<Tag color="red">Deleted</Tag>
								)}
							</div>
							<Text style={{ fontSize: 15, color: "#595959", display: "block", marginTop: 2 }}>
								{asset!.assetType?.name || "Unknown Type"}
								{asset!.assetTypeCategory?.name ? ` / ${asset!.assetTypeCategory.name}` : ""}
							</Text>

							{locationDisplay && (
								<div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
									<EnvironmentOutlined style={{ color: "#1890ff", fontSize: 14 }} />
									<Text style={{ fontSize: 13, color: "#8c8c8c" }}>{locationDisplay}</Text>
								</div>
							)}

							<div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
								<Tag
									color={
										asset!.source === "VISION_CAPTURE"
											? "blue"
											: asset!.source === "IMPORT"
												? "green"
												: asset!.source === "CHILD"
													? "purple"
													: "default"
									}
									style={{ margin: 0 }}
								>
									{asset!.source === "VISION_CAPTURE"
										? "Vision Capture"
										: asset!.source === "IMPORT"
											? "Import"
											: asset!.source === "CHILD"
												? "Child Asset"
												: "Form Submission"}
								</Tag>
								{asset!.submittingUserName && (
									<Text type="secondary" style={{ fontSize: 13 }}>
										by {asset!.submittingUserName}
									</Text>
								)}
								<Text type="secondary" style={{ fontSize: 12 }}>
									Created {formatDate(asset!.createdTimestamp)}
								</Text>
							</div>
						</div>

						{/* Bottom: Stats Row */}
						<div
							style={{
								display: "flex",
								gap: 24,
								flexWrap: "wrap",
								marginTop: 16,
								padding: "14px 0 0 0",
								borderTop: "1px solid #f0f0f0",
							}}
						>
							{/* Health Score */}
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<Progress
									type="circle"
									percent={healthScore}
									size={40}
									strokeColor={getHealthScoreColor(healthScore)}
									format={(p) => `${p}`}
									strokeWidth={8}
								/>
								<div>
									<Text type="secondary" style={{ fontSize: 11, display: "block", lineHeight: 1.2 }}>
										Health
									</Text>
									<Text strong style={{ fontSize: 14, color: getHealthScoreColor(healthScore) }}>
										{healthScore}%
									</Text>
								</div>
							</div>

							{/* Condition */}
							<div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
								<Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
									Condition
								</Text>
								<Tag
									color={getConditionColor(asset!.condition)}
									style={{ marginTop: 2, width: "fit-content" }}
								>
									{asset!.condition || "Unknown"}
								</Tag>
							</div>

							{/* Replacement Year */}
							<div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
								<Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
									Replacement Year
								</Text>
								<Text strong style={{ fontSize: 14, marginTop: 2 }}>
									<CalendarOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
									{replacementYear || "TBD"}
								</Text>
							</div>

							{/* Remaining Life */}
							<div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
								<Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
									Remaining Life
								</Text>
								<Text strong style={{ fontSize: 14, marginTop: 2 }}>
									{remainingLife !== null ? `${remainingLife} years` : "Unknown"}
								</Text>
							</div>
						</div>
					</div>
				</div>
			</Card>
		);
	};

	// ===================================
	// RENDER COMPONENTS
	// ===================================

	const SummaryCard = ({
		title,
		icon,
		children,
	}: {
		title: string;
		icon: React.ReactNode;
		children: React.ReactNode;
	}) => (
		<Card
			title={
				<Space>
					{icon}
					<span>{title}</span>
				</Space>
			}
			style={{ marginBottom: "16px" }}
		>
			{children}
		</Card>
	);

	// Helper function to render editable field
	const renderEditableField = (
		label: string,
		fieldName: keyof UpdateAssetRequest,
		value: any,
		type: "text" | "select" | "date" | "textarea" = "text",
		options?: Array<{ id: number; name: string }>,
		displayValue?: string,
	) => {
		if (!isEditMode) {
			return displayValue ?? value ?? "Not available";
		}

		const currentValue = formValues[fieldName] ?? value;

		switch (type) {
			case "select":
				return (
					<Select
						key={fieldName}
						value={currentValue}
						onChange={(val) =>
							setFormValues((prev) => ({ ...prev, [fieldName]: val }))
						}
						style={{ width: "100%" }}
						loading={loadingOptions}
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
						}
						options={options?.map((opt) => ({
							value: opt.id,
							label: opt.name,
						}))}
						placeholder="Select..."
					/>
				);
			case "date":
				return (
					<DatePicker
						key={fieldName}
						value={currentValue ? dayjs(currentValue) : null}
						onChange={(date) =>
							setFormValues((prev) => ({
								...prev,
								[fieldName]: date?.format("YYYY-MM-DD") || undefined,
							}))
						}
						style={{ width: "100%" }}
						format="MM/DD/YYYY"
					/>
				);
			case "textarea":
				return (
					<Input.TextArea
						key={fieldName}
						ref={(ref) => {
							if (ref) inputRefs.current[fieldName] = ref;
						}}
						value={currentValue || ""}
						onChange={(e) => {
							const target = e.target;
							const position = target.selectionStart ?? 0;
							cursorPositionRef.current = position;
							focusedInputRef.current = fieldName;
							setFormValues((prev) => ({
								...prev,
								[fieldName]: e.target.value,
							}));
						}}
						onFocus={() => {
							focusedInputRef.current = fieldName;
						}}
						onSelect={(e: any) => {
							cursorPositionRef.current = e.target.selectionStart ?? 0;
						}}
						rows={4}
						placeholder="Enter notes..."
						dir="ltr"
						style={{ direction: "ltr", textAlign: "left" }}
					/>
				);
			default:
				return (
					<Input
						key={fieldName}
						ref={(ref) => {
							if (ref) inputRefs.current[fieldName] = ref;
						}}
						value={currentValue || ""}
						onChange={(e) => {
							const target = e.target;
							const position = target.selectionStart ?? 0;
							cursorPositionRef.current = position;
							focusedInputRef.current = fieldName;
							setFormValues((prev) => ({
								...prev,
								[fieldName]: e.target.value,
							}));
						}}
						onFocus={() => {
							focusedInputRef.current = fieldName;
						}}
						onSelect={(e: any) => {
							cursorPositionRef.current = e.target.selectionStart ?? 0;
						}}
						placeholder={`Enter ${label.toLowerCase()}...`}
					/>
				);
		}
	};

	const renderPhotos = () => {
		const photoTypeOptions = [
			{ label: "Overview", value: "OVERVIEW" },
			{ label: "Asset Tag", value: "ASSET_TAG" },
			{ label: "Manufacturer Plate", value: "MANUFACTURER_PLATE" },
			{ label: "Front", value: "FRONT" },
			{ label: "Back", value: "BACK" },
			{ label: "Left", value: "LEFT" },
			{ label: "Right", value: "RIGHT" },
			{ label: "Other", value: "OTHER" },
		];

		return (
			<div>
				{/* Upload section - only visible in edit mode */}
				{isEditMode && (
					<div
						style={{
							marginBottom: "24px",
							padding: "16px",
							backgroundColor: "#f5f5f5",
							borderRadius: "8px",
						}}
					>
						<Space direction="vertical" style={{ width: "100%" }}>
							<Space>
								<span>Photo Type:</span>
								<Select
									value={photoType}
									onChange={setPhotoType}
									style={{ width: 200 }}
									options={photoTypeOptions}
								/>
							</Space>
							<Upload
								accept="image/*"
								showUploadList={false}
								customRequest={({ file, onSuccess, onError }) => {
									handlePhotoUpload(file as RcFile)
										.then((success) => {
											if (success) onSuccess?.("ok");
											else onError?.(new Error("Upload failed"));
										})
										.catch((err) => onError?.(err));
								}}
								disabled={uploadingPhoto}
							>
								<Button
									icon={<UploadOutlined />}
									loading={uploadingPhoto}
									type="primary"
								>
									{uploadingPhoto ? "Uploading..." : "Upload Photo"}
								</Button>
							</Upload>
						</Space>
					</div>
				)}

				{/* Photos grid */}
				{!asset?.photos || asset.photos.length === 0 ? (
					<div
						style={{
							textAlign: "center",
							padding: "20px",
							color: "#999",
							border: "1px dashed #d9d9d9",
							borderRadius: "6px",
						}}
					>
						<CameraOutlined style={{ fontSize: "24px", marginBottom: "8px" }} />
						<div>No photos available</div>
					</div>
				) : (
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
							gap: "16px",
						}}
					>
						{asset.photos.map((photo, index) => (
							<div
								key={photo.id || index}
								style={{ position: "relative", textAlign: "center" }}
							>
								<Image
									src={photo.url}
									alt={`Asset photo - ${photo.type}`}
									style={{
										width: "100%",
										height: "150px",
										objectFit: "cover",
										borderRadius: "6px",
									}}
									preview={{
										mask: <div>Click to preview</div>,
									}}
								/>
								<div
									style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
								>
									{photo.type
										.replace(/_/g, " ")
										.toLowerCase()
										.replace(/\b\w/g, (l) => l.toUpperCase())}
								</div>
								{/* Delete button - only visible in edit mode */}
								{isEditMode && (
									<Button
										danger
										size="small"
										icon={<DeleteOutlined />}
										onClick={() => handlePhotoDelete(photo.id)}
										style={{
											position: "absolute",
											top: "8px",
											right: "8px",
											zIndex: 1,
										}}
									/>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		);
	};

	// ===================================
	// RENDER
	// ===================================

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" tip="Loading asset details..." />
			</div>
		);
	}

	if (!asset) {
		return (
			<div style={{ padding: "24px" }}>
				{/* Header with Back Button */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "24px",
						paddingBottom: "16px",
						borderBottom: "1px solid #f0f0f0",
					}}
				>
					<div>
						<Title level={2} style={{ margin: 0 }}>
							Asset Not Found
						</Title>
						<Text type="secondary">The requested asset could not be found</Text>
					</div>
					<Button icon={<ArrowLeftOutlined />} onClick={onBack} type="primary">
						Back to Assets Table
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div style={{ padding: "24px" }}>
			{/* =============== DUPLICATE ERROR BANNER =============== */}
			{duplicateError && (
				<Alert
					message="Duplicate Asset Tag Error"
					description={
						<div>
							Asset tag <strong>{duplicateError.assetTag}</strong> already
							exists on{" "}
							<a
								href={`#`}
								onClick={(e) => {
									e.preventDefault();
									if (onNavigateToAsset) {
										onNavigateToAsset(duplicateError.assetId.toString());
									}
								}}
								style={{
									fontWeight: "bold",
									textDecoration: "underline",
									cursor: "pointer",
								}}
							>
								Asset #{duplicateError.assetId}
							</a>{" "}
							at location: <strong>{duplicateError.locationInfo}</strong>
						</div>
					}
					type="error"
					closable
					onClose={() => setDuplicateError(null)}
					showIcon
					style={{ marginBottom: "24px" }}
				/>
			)}

			{/* =============== HERO PROFILE SECTION =============== */}
			{renderHeroSection()}

			{/* =============== ASSET DETAILS (consolidated) =============== */}
			<Card
				style={{
					marginBottom: 16,
					borderRadius: 10,
					boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
				}}
				bodyStyle={{ padding: "20px 24px" }}
			>
				<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600, display: "block", marginBottom: 14 }}>
					<InfoCircleOutlined style={{ marginRight: 6 }} />
					Asset Details
				</Text>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
						gap: "20px 32px",
					}}
				>
					{/* ---- Asset Tag (edit mode only — shown in hero when not editing) ---- */}
					{isEditMode && (
						<div>
							<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
								Asset Tag
							</Text>
							{renderEditableField("Asset Tag", "assetTag", asset.assetTag)}
						</div>
					)}

					{/* ---- Asset Type (edit mode only) ---- */}
					{isEditMode && (
						<div>
							<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
								Asset Type
							</Text>
							{renderEditableField("Asset Type", "organizationAssetTypeId", asset.organizationAssetTypeId, "select", assetTypes, asset.assetType?.name)}
						</div>
					)}

					{/* ---- Condition (edit mode only) ---- */}
					{isEditMode && (
						<div>
							<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
								Condition
							</Text>
							{renderEditableField("Condition", "condition", asset.condition)}
						</div>
					)}

					{/* ---- Asset Category ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Category
						</Text>
						<Text strong style={{ fontSize: 14 }}>
							{asset.assetTypeCategory?.name || "—"}
						</Text>
					</div>

					{/* ---- Date of Birth ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Date of Birth
						</Text>
						{isEditMode ? (
							renderEditableField("Date of Birth", "dateOfBirth", asset.dateOfBirth, "date", undefined, formatDate(asset.dateOfBirth))
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{formatDate(asset.dateOfBirth) || "—"}
								{asset.isDobEstimate && (
									<Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>Est.</Tag>
								)}
							</Text>
						)}
					</div>

					{/* ---- Last Updated ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Last Updated
						</Text>
						<Text strong style={{ fontSize: 14 }}>
							{formatDate(asset.updatedTimestamp) || "—"}
						</Text>
					</div>

					{/* ---- Last Audit ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Last Audit
						</Text>
						<Text strong style={{ fontSize: 14 }}>
							{formatDate(asset.lastAudit) || "—"}
						</Text>
					</div>

					{/* ---- Last Health Update ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Last Health Update
						</Text>
						<Text strong style={{ fontSize: 14 }}>
							{formatDate(asset.healthScore?.lastUpdate) || "—"}
						</Text>
					</div>

					{/* ---- Address ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Address
						</Text>
						<Text strong style={{ fontSize: 14 }}>
							{asset.location?.address1
								? `${asset.location.address1}${asset.location.address2 ? `, ${asset.location.address2}` : ""}`
								: "—"}
						</Text>
						{(asset.location?.city || asset.location?.state || asset.location?.zip) && (
							<Text style={{ fontSize: 13, display: "block", color: "#595959" }}>
								{`${asset.location?.city || ""} ${asset.location?.state || ""} ${asset.location?.zip || ""}`.trim()}
							</Text>
						)}
					</div>

					{/* ---- Equipment Area ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Equipment Area
						</Text>
						{isEditMode ? (
							renderEditableField("Equipment Area", "organizationEquipmentAreaId", asset.organizationEquipmentAreaId, "select", equipmentAreas, asset.equipmentArea?.name)
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{asset.equipmentArea?.name || "—"}
							</Text>
						)}
					</div>

					{/* ---- Equipment Sub Area ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Equipment Sub Area
						</Text>
						<Text strong style={{ fontSize: 14 }}>
							{asset.equipmentSubArea?.name || "—"}
						</Text>
					</div>

					{/* ---- Power Source ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Power Source
						</Text>
						{isEditMode ? (
							renderEditableField("Power Source", "powerSource", asset.powerSource)
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{asset.powerSource || "—"}
							</Text>
						)}
					</div>
				</div>
			</Card>

			{/* =============== MANUFACTURER & MODEL =============== */}
			<Card
				style={{
					marginBottom: 16,
					borderRadius: 10,
					boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
				}}
				bodyStyle={{ padding: "20px 24px" }}
			>
				<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600, display: "block", marginBottom: 14 }}>
					<TagOutlined style={{ marginRight: 6 }} />
					Manufacturer & Model
				</Text>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
						gap: "20px 32px",
					}}
				>
					{/* ---- Manufacturer ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Manufacturer
						</Text>
						{isEditMode ? (
							renderEditableField("Manufacturer", "manufacturerId", asset.manufacturer?.id, "select", manufacturers, asset.manufacturer?.name)
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{asset.manufacturer?.name || "—"}
								{asset.isManufacturerNotFound && (
									<Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Not Found</Tag>
								)}
							</Text>
						)}
					</div>

					{/* ---- Model Number ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Model Number
						</Text>
						{isEditMode ? (
							renderEditableField("Model Number", "modelNumber", asset.modelNumber)
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{asset.modelNumber || "—"}
								{asset.isModelNotFound && (
									<Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Not Found</Tag>
								)}
							</Text>
						)}
					</div>

					{/* ---- Serial Number ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Serial Number
						</Text>
						{isEditMode ? (
							renderEditableField("Serial Number", "serialNumber", asset.serialNumber)
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{asset.serialNumber || "—"}
								{asset.isSerialNumberNotFound && (
									<Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Not Found</Tag>
								)}
							</Text>
						)}
					</div>

					{/* ---- Manufacturer Date ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Manufacturer Date
						</Text>
						{isEditMode ? (
							renderEditableField("Manufacturer Date", "manufacturerDate", asset.manufacturerDate, "date", undefined, formatDate(asset.manufacturerDate))
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{formatDate(asset.manufacturerDate) || "—"}
								{asset.isManufacturerDateNotFound && (
									<Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Not Found</Tag>
								)}
							</Text>
						)}
					</div>

					{/* ---- Temp Alert Tag ---- */}
					<div>
						<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 4 }}>
							Temp Alert Tag
						</Text>
						{isEditMode ? (
							renderEditableField("Temp Alert Tag", "tempAlertTag", asset.tempAlertTag)
						) : (
							<Text strong style={{ fontSize: 14 }}>
								{asset.tempAlertTag || "—"}
								{asset.isTempAlertNotFound && (
									<Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Not Found</Tag>
								)}
							</Text>
						)}
					</div>
				</div>
			</Card>

			{/* =============== CHILD ASSETS =============== */}
			<Card
				style={{
					marginBottom: 16,
					borderRadius: 10,
					boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
				}}
				bodyStyle={{ padding: "20px 24px" }}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 14,
					}}
				>
					<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}>
						<ApartmentOutlined style={{ marginRight: 6 }} />
						Child Assets{childAssets.length > 0 ? ` (${childAssets.length})` : ""}
					</Text>
					{!asset.isDeleted && (
						<Button size="small" icon={<ApartmentOutlined />} onClick={openAddChildModal}>
							Add Child Asset
						</Button>
					)}
				</div>
				{childAssets.length === 0 && !loadingChildren ? (
					<Text type="secondary" style={{ fontSize: 13 }}>
						No child assets. Use "Add Child Asset" to track components or
						sub-equipment under this asset — children inherit this asset's
						location and get a virtual tag derived from its tag.
					</Text>
				) : (
					<Table
						dataSource={childAssets}
						loading={loadingChildren}
						rowKey="id"
						size="small"
						pagination={false}
						onRow={(record: ChildAssetRow) => ({
							onClick: () => onNavigateToAsset?.(record.id.toString()),
							style: { cursor: "pointer" },
						})}
						columns={[
							{
								title: "Asset Tag",
								dataIndex: "assetTag",
								key: "assetTag",
								render: (text: string, record: ChildAssetRow) => (
									<Space size={6}>
										<Text strong style={{ fontSize: 13 }}>
											{text || `Asset #${record.id}`}
										</Text>
										{record.tagType === "VIRTUAL" && (
											<Tag color="purple" style={{ fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
												Virtual
											</Tag>
										)}
										{record.childCount > 0 && (
											<Tag color="blue" style={{ fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
												{record.childCount} {record.childCount === 1 ? "child" : "children"}
											</Tag>
										)}
									</Space>
								),
							},
							{
								title: "Type",
								dataIndex: "assetTypeName",
								key: "assetTypeName",
								render: (text: string) => text || "—",
							},
							{
								title: "Manufacturer",
								dataIndex: "manufacturerName",
								key: "manufacturerName",
								render: (text: string) => text || "—",
							},
							{
								title: "Model",
								dataIndex: "modelNumber",
								key: "modelNumber",
								render: (text: string) => text || "—",
							},
							{
								title: "Serial #",
								dataIndex: "serialNumber",
								key: "serialNumber",
								render: (text: string) => text || "—",
							},
							{
								title: "Condition",
								dataIndex: "condition",
								key: "condition",
								render: (text: string) =>
									text ? <Tag color={getConditionColor(text)}>{text}</Tag> : "—",
							},
						]}
					/>
				)}
			</Card>

			{/* =============== NYCHA ELEVATOR INFORMATION =============== */}
			{asset.nychaElevator && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard
							title="NYCHA Elevator Information"
							icon={<SettingOutlined />}
						>
							<Row gutter={[16, 16]}>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="Elevator Details"
									>
										<Descriptions.Item label="Elevator Bank">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorBankName"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorBankName ??
														asset.nychaElevator.elevator_bank_name ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorBankName";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorBankName: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorBankName";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter elevator bank name..."
												/>
											) : (
												asset.nychaElevator.elevator_bank_name ||
												"Not available"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Elevator Number">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorNumber"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorNumber ??
														asset.nychaElevator.elevator_number ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorNumber";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorNumber: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorNumber";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter elevator number..."
												/>
											) : (
												asset.nychaElevator.elevator_number || "Not available"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Type">
											{isEditMode ? (
												<Select
													value={
														(formValues as any).nychaElevatorType ??
														asset.nychaElevator.elevator_type
													}
													onChange={(val) =>
														setFormValues(
															(prev) =>
																({ ...prev, nychaElevatorType: val }) as any,
														)
													}
													style={{ width: "100%" }}
													options={[
														{ label: "Passenger", value: "PASSENGER" },
														{ label: "Freight", value: "FREIGHT" },
														{ label: "Service", value: "SERVICE" },
													]}
												/>
											) : (
												<Tag color="blue">
													{asset.nychaElevator.elevator_type}
												</Tag>
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Sub Type">
											{isEditMode ? (
												<Select
													value={
														(formValues as any).nychaElevatorSubType ??
														asset.nychaElevator.elevator_sub_type
													}
													onChange={(val) =>
														setFormValues(
															(prev) =>
																({ ...prev, nychaElevatorSubType: val }) as any,
														)
													}
													style={{ width: "100%" }}
													allowClear
													placeholder="Select sub type..."
													options={[
														{ label: "Traction", value: "TRACTION" },
														{ label: "Hydraulic", value: "HYDRAULIC" },
													]}
												/>
											) : asset.nychaElevator.elevator_sub_type ? (
												<Tag>{asset.nychaElevator.elevator_sub_type}</Tag>
											) : (
												"Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Building">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorBuilding"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorBuilding ??
														asset.nychaElevator.building_name ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorBuilding";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorBuilding: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorBuilding";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter building name..."
												/>
											) : (
												asset.nychaElevator.building_name || "Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Wing/Section">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorWingSection"] =
																ref;
													}}
													value={
														(formValues as any).nychaElevatorWingSection ??
														asset.nychaElevator.wing_section ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current =
															"nychaElevatorWingSection";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorWingSection: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current =
															"nychaElevatorWingSection";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter wing/section..."
												/>
											) : (
												asset.nychaElevator.wing_section || "Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Floor">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorFloor"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorFloor ??
														asset.nychaElevator.floor ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorFloor";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorFloor: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorFloor";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter floor..."
												/>
											) : (
												asset.nychaElevator.floor || "Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Capacity (lbs)">
											{isEditMode ? (
												<InputNumber
													value={
														(formValues as any).nychaElevatorCapacity ??
														asset.nychaElevator.capacity
													}
													onChange={(val) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorCapacity: val,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													placeholder="Enter capacity..."
												/>
											) : asset.nychaElevator.capacity ? (
												`${asset.nychaElevator.capacity} lbs`
											) : (
												"Not specified"
											)}
										</Descriptions.Item>
									</Descriptions>
								</Col>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="Maximo Status"
									>
										<Descriptions.Item label="Status">
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<Tag
													color={
														asset.nychaElevator.maximo_status === "ACTIVE"
															? "green"
															: asset.nychaElevator.maximo_status === "DOWN"
																? "red"
																: asset.nychaElevator.maximo_status ===
																		"MAINTENANCE"
																	? "orange"
																	: "default"
													}
												>
													{asset.nychaElevator.maximo_status
														? asset.nychaElevator.maximo_status === "ACTIVE"
															? "● IN SERVICE"
															: asset.nychaElevator.maximo_status === "DOWN"
																? "● OUT OF SERVICE"
																: asset.nychaElevator.maximo_status
														: "Not Connected"}
												</Tag>
												<Button
													size="small"
													icon={<ToolOutlined />}
													onClick={() =>
														message.info("Refreshing Maximo status...")
													}
												>
													Refresh
												</Button>
											</div>
										</Descriptions.Item>
										<Descriptions.Item label="Maximo Asset ID">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaMaximoAssetId"] = ref;
													}}
													value={
														(formValues as any).nychaMaximoAssetId ??
														asset.nychaElevator.maximo_asset_id ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaMaximoAssetId";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaMaximoAssetId: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaMaximoAssetId";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter Maximo Asset ID..."
												/>
											) : (
												asset.nychaElevator.maximo_asset_id || "Not assigned"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Active Alerts">
											<Tag
												color={
													asset.nychaElevator.active_alerts_count > 0
														? "red"
														: "green"
												}
											>
												{asset.nychaElevator.active_alerts_count} alert
												{asset.nychaElevator.active_alerts_count !== 1
													? "s"
													: ""}
											</Tag>
										</Descriptions.Item>
										<Descriptions.Item label="Open Violations">
											<Tag
												color={
													asset.nychaElevator.open_violations_count > 0
														? "orange"
														: "green"
												}
											>
												{asset.nychaElevator.open_violations_count} violation
												{asset.nychaElevator.open_violations_count !== 1
													? "s"
													: ""}
											</Tag>
										</Descriptions.Item>
									</Descriptions>
								</Col>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="REMS Information"
									>
										<Descriptions.Item label="REMS Status">
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<Tag
													color={
														asset.nychaElevator.rems_asset_id
															? "blue"
															: "default"
													}
												>
													{asset.nychaElevator.rems_asset_id
														? "Connected"
														: "Not Connected"}
												</Tag>
												<Button
													size="small"
													icon={<ToolOutlined />}
													onClick={() =>
														message.info("Refreshing REMS data...")
													}
												>
													Refresh
												</Button>
											</div>
										</Descriptions.Item>
										<Descriptions.Item label="REMS Asset ID">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaRemsAssetId"] = ref;
													}}
													value={
														(formValues as any).nychaRemsAssetId ??
														asset.nychaElevator.rems_asset_id ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaRemsAssetId";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaRemsAssetId: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaRemsAssetId";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter REMS Asset ID..."
												/>
											) : (
												asset.nychaElevator.rems_asset_id || "Not assigned"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="REMS Location ID">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaRemsLocationId"] = ref;
													}}
													value={
														(formValues as any).nychaRemsLocationId ??
														asset.nychaElevator.rems_location_id ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaRemsLocationId";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaRemsLocationId: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaRemsLocationId";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter REMS Location ID..."
												/>
											) : (
												asset.nychaElevator.rems_location_id || "Not assigned"
											)}
										</Descriptions.Item>
									</Descriptions>
								</Col>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="Compliance & Testing"
									>
										<Descriptions.Item label="Last Periodic Inspection">
											{isEditMode ? (
												<DatePicker
													value={
														(formValues as any).nychaLastPeriodicInspection
															? dayjs(
																	(formValues as any)
																		.nychaLastPeriodicInspection,
																)
															: asset.nychaElevator.last_periodic_inspection
																? dayjs(
																		asset.nychaElevator
																			.last_periodic_inspection,
																	)
																: null
													}
													onChange={(date) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaLastPeriodicInspection:
																		date?.format("YYYY-MM-DD") || undefined,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													format="MM/DD/YYYY"
												/>
											) : asset.nychaElevator.last_periodic_inspection ? (
												formatDate(asset.nychaElevator.last_periodic_inspection)
											) : (
												"No data"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Last Cat 1 Test">
											{isEditMode ? (
												<DatePicker
													value={
														(formValues as any).nychaLastCat1Test
															? dayjs((formValues as any).nychaLastCat1Test)
															: asset.nychaElevator.last_cat1_test
																? dayjs(asset.nychaElevator.last_cat1_test)
																: null
													}
													onChange={(date) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaLastCat1Test:
																		date?.format("YYYY-MM-DD") || undefined,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													format="MM/DD/YYYY"
												/>
											) : asset.nychaElevator.last_cat1_test ? (
												formatDate(asset.nychaElevator.last_cat1_test)
											) : (
												"No data"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Last Cat 5 Test">
											{isEditMode ? (
												<DatePicker
													value={
														(formValues as any).nychaLastCat5Test
															? dayjs((formValues as any).nychaLastCat5Test)
															: asset.nychaElevator.last_cat5_test
																? dayjs(asset.nychaElevator.last_cat5_test)
																: null
													}
													onChange={(date) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaLastCat5Test:
																		date?.format("YYYY-MM-DD") || undefined,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													format="MM/DD/YYYY"
												/>
											) : asset.nychaElevator.last_cat5_test ? (
												formatDate(asset.nychaElevator.last_cat5_test)
											) : (
												"No data"
											)}
										</Descriptions.Item>
									</Descriptions>
								</Col>
							</Row>
						</SummaryCard>
					</Col>
				</Row>
			)}

			{/* =============== TECHNICAL SPECIFICATIONS =============== */}
			{(asset.isElectrical || asset.isRefrigeration) && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					{asset.isElectrical && asset.electricalDetails && (
						<Col xs={24} lg={12}>
							<SummaryCard title="Electrical Details" icon={<ToolOutlined />}>
								<Descriptions column={1} bordered size="small">
									<Descriptions.Item label="Amps">
										{asset.electricalDetails.amps || "Not specified"}
										{asset.electricalDetails.isAmpsNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Voltage">
										{asset.electricalDetails.voltage || "Not specified"}
										{asset.electricalDetails.isVoltageNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Wattage">
										{asset.electricalDetails.wattage || "Not specified"}
										{asset.electricalDetails.isWattageNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
								</Descriptions>
							</SummaryCard>
						</Col>
					)}

					{asset.isRefrigeration && asset.refrigerationDetails && (
						<Col xs={24} lg={12}>
							<SummaryCard
								title="Refrigeration Details"
								icon={<ToolOutlined />}
							>
								<Descriptions column={1} bordered size="small">
									<Descriptions.Item label="Refrigerant Type">
										{asset.refrigerationDetails.refrigerantType ||
											"Not specified"}
										{asset.refrigerationDetails.isRefrigerantNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Refrigerant Charge">
										{asset.refrigerationDetails.refrigerantCharge &&
										asset.refrigerationDetails.refrigerantChargeMeasurementUnit
											? `${asset.refrigerationDetails.refrigerantCharge} ${asset.refrigerationDetails.refrigerantChargeMeasurementUnit}`
											: "Not specified"}
										{asset.refrigerationDetails.isChargeNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Usage">
										{asset.refrigerationDetails.usage || "Not specified"}
									</Descriptions.Item>
								</Descriptions>
							</SummaryCard>
						</Col>
					)}
				</Row>
			)}

			{/* =============== AI DEEP DIVE =============== */}
			{asset.aiDeepDive && Object.keys(asset.aiDeepDive).length > 0 && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard title="AI Deep Dive" icon={<ExperimentOutlined />}>
							{(() => {
								const deepDive = asset.aiDeepDive!;

								// Field display configuration
								const fieldConfig: Record<string, { label: string; suffix?: string }> = {
									btuRating: { label: "BTU Rating", suffix: " BTU/h" },
									tonnage: { label: "Tonnage", suffix: " tons" },
									seerRating: { label: "SEER Rating" },
									eerRating: { label: "EER Rating" },
									powerSupplyPhase: { label: "Phase" },
									partNumber: { label: "Part Number" },
								};

								// Confidence color helper
								const getConfidenceColor = (confidence: number) => {
									if (confidence >= 0.90) return "green";
									if (confidence >= 0.75) return "blue";
									if (confidence >= 0.60) return "orange";
									return "red";
								};

								// Separate raw plate text from structured fields
								const structuredFields = Object.entries(deepDive)
									.filter(([key]) => key !== "rawPlateText")
									.filter(([, val]) => val?.text !== undefined && val?.text !== null);

								const rawPlateText = deepDive.rawPlateText;

								return (
									<>
										{structuredFields.length > 0 && (
											<Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
												{structuredFields.map(([key, field]) => {
													const config = fieldConfig[key] || { label: key };
													const displayValue = field.text + (config.suffix || "");
													const confPct = Math.round(field.confidence * 100);
													return (
														<Descriptions.Item
															key={key}
															label={config.label}
														>
															<Space>
																<Text>{displayValue}</Text>
																<Tag
																	color={getConfidenceColor(field.confidence)}
																	style={{ fontSize: "11px" }}
																>
																	{confPct}%
																</Tag>
															</Space>
														</Descriptions.Item>
													);
												})}
											</Descriptions>
										)}

										{rawPlateText?.text && (
											<div style={{ marginTop: structuredFields.length > 0 ? "12px" : 0 }}>
												<Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
													Raw Plate Text
												</Text>
												<div
													style={{
														background: "#f5f5f5",
														borderRadius: "6px",
														padding: "10px 14px",
														fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
														fontSize: "12px",
														lineHeight: "1.6",
														whiteSpace: "pre-wrap",
														wordBreak: "break-word",
														border: "1px solid #e8e8e8",
													}}
												>
													{String(rawPlateText.text).split(" | ").join("\n")}
												</div>
											</div>
										)}
									</>
								);
							})()}
						</SummaryCard>
					</Col>
				</Row>
			)}

			{/* =============== WARRANTY INFORMATION =============== */}
			{(asset.warranty || isEditMode) && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard
							title="Warranty Information"
							icon={<InfoCircleOutlined />}
						>
							{isEditMode ? (
								<Row gutter={16}>
									<Col span={8}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Has Warranty
											</label>
											<Select
												value={
													formValues.warranty?.hasWarranty ??
													asset.warranty?.hasWarranty ??
													undefined
												}
												onChange={(value) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															hasWarranty: value,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Select"
											>
												<Select.Option value={true}>Yes</Select.Option>
												<Select.Option value={false}>No</Select.Option>
											</Select>
										</div>
									</Col>
									<Col span={8}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Warranty Status
											</label>
											<Select
												value={
													formValues.warranty?.isWarrantyExpired ??
													asset.warranty?.isWarrantyExpired ??
													undefined
												}
												onChange={(value) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															isWarrantyExpired: value,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Select status"
											>
												<Select.Option value={false}>Active</Select.Option>
												<Select.Option value={true}>Expired</Select.Option>
											</Select>
										</div>
									</Col>
									<Col span={8}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Not Found
											</label>
											<Select
												value={
													formValues.warranty?.isNotFound ??
													asset.warranty?.isNotFound ??
													undefined
												}
												onChange={(value) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															isNotFound: value,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Select"
											>
												<Select.Option value={false}>Found</Select.Option>
												<Select.Option value={true}>Not Found</Select.Option>
											</Select>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Start Date
											</label>
											<DatePicker
												value={
													formValues.warranty?.startDate
														? dayjs(formValues.warranty.startDate)
														: asset.warranty?.startDate
															? dayjs(asset.warranty.startDate)
															: undefined
												}
												onChange={(date) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															startDate: date?.format("YYYY-MM-DD"),
														},
													}))
												}
												style={{ width: "100%" }}
												format="YYYY-MM-DD"
											/>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Expiration Date
											</label>
											<DatePicker
												value={
													formValues.warranty?.expirationDate
														? dayjs(formValues.warranty.expirationDate)
														: asset.warranty?.expirationDate
															? dayjs(asset.warranty.expirationDate)
															: undefined
												}
												onChange={(date) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															expirationDate: date?.format("YYYY-MM-DD"),
														},
													}))
												}
												style={{ width: "100%" }}
												format="YYYY-MM-DD"
											/>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Warranty Type
											</label>
											<Input
												ref={(ref) => {
													if (ref) inputRefs.current["warranty.type"] = ref;
												}}
												value={
													formValues.warranty?.type ??
													asset.warranty?.type ??
													""
												}
												onChange={(e) => {
													const target = e.target;
													const position = target.selectionStart ?? 0;
													cursorPositionRef.current = position;
													focusedInputRef.current = "warranty.type";
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															type: e.target.value,
														},
													}));
												}}
												onFocus={() => {
													focusedInputRef.current = "warranty.type";
												}}
												onSelect={(e: any) => {
													cursorPositionRef.current =
														e.target.selectionStart ?? 0;
												}}
												placeholder="e.g., Manufacturer, Extended"
											/>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Term (months)
											</label>
											<InputNumber
												value={
													formValues.warranty?.term ??
													asset.warranty?.term ??
													undefined
												}
												onChange={(value: number | null) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															term: value || undefined,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Enter term in months"
												min={0}
											/>
										</div>
									</Col>
									<Col span={24}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Provider
											</label>
											<Input
												ref={(ref) => {
													if (ref) inputRefs.current["warranty.provider"] = ref;
												}}
												value={
													formValues.warranty?.provider ??
													asset.warranty?.provider ??
													""
												}
												onChange={(e) => {
													const target = e.target;
													const position = target.selectionStart ?? 0;
													cursorPositionRef.current = position;
													focusedInputRef.current = "warranty.provider";
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															provider: e.target.value,
														},
													}));
												}}
												onFocus={() => {
													focusedInputRef.current = "warranty.provider";
												}}
												onSelect={(e: any) => {
													cursorPositionRef.current =
														e.target.selectionStart ?? 0;
												}}
												placeholder="Warranty provider name"
											/>
										</div>
									</Col>
								</Row>
							) : (
								<Descriptions column={2} bordered size="small">
									<Descriptions.Item label="Has Warranty">
										{asset.warranty?.hasWarranty !== undefined ? (
											<Tag color={asset.warranty.hasWarranty ? "green" : "red"}>
												{asset.warranty.hasWarranty ? "Yes" : "No"}
											</Tag>
										) : (
											"Not specified"
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Warranty Status">
										{asset.warranty?.isWarrantyExpired !== undefined && (
											<Tag
												color={
													asset.warranty.isWarrantyExpired ? "red" : "green"
												}
											>
												{asset.warranty.isWarrantyExpired
													? "Expired"
													: "Active"}
											</Tag>
										)}
										{asset.warranty?.isNotFound && (
											<Tag color="orange">Not Found</Tag>
										)}
										{!asset.warranty?.isWarrantyExpired &&
											!asset.warranty?.isNotFound &&
											asset.warranty?.isWarrantyExpired === undefined &&
											"Not specified"}
									</Descriptions.Item>
									<Descriptions.Item label="Start Date">
										{formatDate(asset.warranty?.startDate)}
									</Descriptions.Item>
									<Descriptions.Item label="Expiration Date">
										{formatDate(asset.warranty?.expirationDate)}
									</Descriptions.Item>
									<Descriptions.Item label="Warranty Type">
										{asset.warranty?.type || "Not specified"}
									</Descriptions.Item>
									<Descriptions.Item label="Term">
										{asset.warranty?.term
											? `${asset.warranty.term} months`
											: "Not specified"}
									</Descriptions.Item>
									<Descriptions.Item label="Provider" span={2}>
										{asset.warranty?.provider || "Not specified"}
									</Descriptions.Item>
								</Descriptions>
							)}
						</SummaryCard>
					</Col>
				</Row>
			)}


			{/* =============== UNIQUE FIELDS =============== */}
			{(isEditMode && orgUniqueFieldDefs.length > 0) ||
			(asset.uniqueFields && parseUniqueFields(asset.uniqueFields)) ? (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard title="Unique Fields" icon={<InfoCircleOutlined />}>
							{isEditMode ? (
								<Descriptions column={1} bordered size="small">
									{(() => {
										const existingFields = parseUniqueFields(asset.uniqueFields) || {};
										const allKeys = new Set([
											...orgUniqueFieldDefs.map((f) => f.name),
											...Object.keys(existingFields),
										]);
										return Array.from(allKeys).map((key) => {
											const fieldDef = orgUniqueFieldDefs.find((f) => f.name === key);
											const currentValue =
												formValues.uniqueFields?.[key] !== undefined
													? formValues.uniqueFields[key]
													: existingFields[key] ?? "";
											const label = fieldDef?.label ||
												key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");

											return (
												<Descriptions.Item key={key} label={label}>
													{fieldDef?.type === "select" && fieldDef.options ? (
														<Select
															value={currentValue || undefined}
															onChange={(val) =>
																setFormValues((prev) => ({
																	...prev,
																	uniqueFields: {
																		...prev.uniqueFields,
																		[key]: val,
																	},
																}))
															}
															placeholder={fieldDef.placeholder || `Select ${label}`}
															style={{ width: "100%" }}
															allowClear
														>
															{fieldDef.options.map((opt) => (
																<Select.Option key={opt} value={opt}>
																	{opt}
																</Select.Option>
															))}
														</Select>
													) : (
														<Input
															key={`unique-field-${key}`}
															ref={(ref) => {
																if (ref)
																	inputRefs.current[`uniqueFields.${key}`] = ref;
															}}
															value={typeof currentValue === "object" ? JSON.stringify(currentValue) : String(currentValue)}
															onChange={(e) => {
																const target = e.target;
																const position = target.selectionStart ?? 0;
																cursorPositionRef.current = position;
																focusedInputRef.current = `uniqueFields.${key}`;
																setFormValues((prev) => ({
																	...prev,
																	uniqueFields: {
																		...prev.uniqueFields,
																		[key]: e.target.value,
																	},
																}));
															}}
															onFocus={() => {
																focusedInputRef.current = `uniqueFields.${key}`;
															}}
															onSelect={(e: any) => {
																cursorPositionRef.current = e.target.selectionStart ?? 0;
															}}
															placeholder={fieldDef?.placeholder || `Enter ${label}`}
														/>
													)}
												</Descriptions.Item>
											);
										});
									})()}
								</Descriptions>
							) : (
								<Descriptions column={1} bordered size="small">
									{Object.entries(
										parseUniqueFields(asset.uniqueFields) || {},
									).map(([key, value]) => {
										const fieldDef = orgUniqueFieldDefs.find((f) => f.name === key);
										const label = fieldDef?.label ||
											key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");
										return (
											<Descriptions.Item key={key} label={label}>
												{typeof value === "object"
													? JSON.stringify(value)
													: String(value)}
											</Descriptions.Item>
										);
									})}
								</Descriptions>
							)}
						</SummaryCard>
					</Col>
				</Row>
			) : null}

			{/* =============== PHOTOS =============== */}
			<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
				<Col xs={24}>
					<SummaryCard title="Asset Photos" icon={<CameraOutlined />}>
						{renderPhotos()}
					</SummaryCard>
				</Col>
			</Row>

			{/* =============== NOTES =============== */}
			<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
				<Col xs={24}>
					<SummaryCard title="General Notes" icon={<InfoCircleOutlined />}>
						{isEditMode ? (
							renderEditableField(
								"General Notes",
								"generalNotes",
								asset.generalNotes,
								"textarea",
							)
						) : (
							<div
								style={{
									padding: "12px",
									backgroundColor: "#f9f9f9",
									borderRadius: "6px",
									whiteSpace: "pre-wrap",
								}}
							>
								{asset.generalNotes || "No notes available"}
							</div>
						)}
					</SummaryCard>
				</Col>
			</Row>

			{/* =============== REVISION HISTORY =============== */}
			{showRevisionHistory && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard title="Revision History" icon={<HistoryOutlined />}>
							{loadingRevisions ? (
								<div style={{ textAlign: "center", padding: "20px" }}>
									<Spin />
								</div>
							) : revisions.length === 0 ? (
								<div
									style={{
										textAlign: "center",
										padding: "20px",
										color: "#999",
										border: "1px dashed #d9d9d9",
										borderRadius: "6px",
									}}
								>
									<HistoryOutlined
										style={{ fontSize: "24px", marginBottom: "8px" }}
									/>
									<div>No revision history available</div>
								</div>
							) : (
								<Timeline>
									{revisions.map((revision) => {
										// Format field name for display
										const fieldLabel = revision.fieldName
											.replace(/([A-Z])/g, " $1")
											.replace(/^./, (str) => str.toUpperCase())
											.trim();

										// Get display values (prefer display values, fall back to raw values)
										const oldValueDisplay =
											revision.oldValueDisplay ??
											(revision.oldValue === null ||
											revision.oldValue === undefined
												? "Empty"
												: String(revision.oldValue));
										const newValueDisplay =
											revision.newValueDisplay ??
											(revision.newValue === null ||
											revision.newValue === undefined
												? "Empty"
												: String(revision.newValue));

										return (
											<Timeline.Item key={revision.id}>
												<div style={{ marginBottom: "8px" }}>
													<Text strong>{fieldLabel}</Text>
													<Text
														type="secondary"
														style={{ marginLeft: "8px", fontSize: "12px" }}
													>
														{formatDate(revision.revisionTimestamp)}
													</Text>
												</div>
												<div style={{ marginLeft: "16px", marginTop: "4px" }}>
													<Text
														type="secondary"
														delete
														style={{ marginRight: "8px" }}
													>
														{oldValueDisplay}
													</Text>
													<Text strong style={{ color: "#52c41a" }}>
														→ {newValueDisplay}
													</Text>
												</div>
												{revision.userName && (
													<div style={{ marginLeft: "16px", marginTop: "4px" }}>
														<Text type="secondary" style={{ fontSize: "12px" }}>
															Changed by: {revision.userName}
															{revision.userEmail && ` (${revision.userEmail})`}
														</Text>
													</div>
												)}
											</Timeline.Item>
										);
									})}
								</Timeline>
							)}
						</SummaryCard>
					</Col>
				</Row>
			)}
			{/* =============== INTEGRATIONS (bottom of page) =============== */}
			{((asset.integrationSyncs && asset.integrationSyncs.length > 0) ||
				(asset.photoSync && asset.photoSync.id)) && (
				<Card
					style={{
						marginTop: 16,
						borderRadius: 10,
						boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
					}}
					bodyStyle={{ padding: "20px 24px" }}
				>
					<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600, display: "block", marginBottom: 16 }}>
						<ApiOutlined style={{ marginRight: 6 }} />
						Integrations
					</Text>

					<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
						{/* CMMS / Data Sync Integrations */}
						{asset.integrationSyncs && asset.integrationSyncs.length > 0 &&
							asset.integrationSyncs.map((sync, index) => {
								const branding = ASSET_INTEGRATION_BRANDING[sync.integrationId] || {
									abbr: (sync.integrationName || "??").substring(0, 2).toUpperCase(),
									color: "#8c8c8c",
									tier: "",
									category: "Integration",
								};
								const syncOk = sync.syncStatus === "success";
								const syncFailed = sync.syncStatus === "failed";
								const syncPending = sync.syncStatus === "pending";

								return (
									<div
										key={index}
										style={{
											display: "flex",
											alignItems: "center",
											gap: 14,
											padding: "12px 16px",
											borderRadius: 8,
											border: `1px solid ${syncOk ? "#b7eb8f" : syncFailed ? "#ffa39e" : "#d9d9d9"}`,
											background: syncOk ? "#f6ffed" : syncFailed ? "#fff2f0" : "#fafafa",
										}}
									>
										{/* Platform logo */}
										<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
											<rect width="40" height="40" rx="8" fill={branding.color} />
											<text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="white" fontFamily="Arial, sans-serif" fontSize={branding.abbr.length > 2 ? "11" : "15"} fontWeight="bold">
												{branding.abbr}
											</text>
										</svg>

										{/* Platform info */}
										<div style={{ flex: 1, minWidth: 0 }}>
											<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
												<Text strong style={{ fontSize: 14 }}>
													{sync.integrationName || `Integration #${sync.orgIntegrationId}`}
												</Text>
												<Tag color="blue" style={{ fontSize: 10, lineHeight: "18px", margin: 0 }}>
													{branding.category}
												</Tag>
												{branding.tier && (
													<Tag color="purple" style={{ fontSize: 10, lineHeight: "18px", margin: 0 }}>
														{branding.tier}
													</Tag>
												)}
											</div>
											<div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
												<Text type="secondary" style={{ fontSize: 12 }}>
													<ClockCircleOutlined style={{ marginRight: 4 }} />
													{sync.lastSyncedAt
														? `Last synced ${new Date(sync.lastSyncedAt).toLocaleString()}`
														: "Never synced"}
												</Text>
											</div>
										</div>

										{/* Status badge */}
										<Tag
											icon={syncOk ? <CheckCircleOutlined /> : syncFailed ? <CloseCircleOutlined /> : <ClockCircleOutlined />}
											color={syncOk ? "success" : syncFailed ? "error" : syncPending ? "processing" : "default"}
											style={{ fontSize: 12, padding: "2px 10px", margin: 0 }}
										>
											{sync.syncStatus?.toUpperCase() || "UNKNOWN"}
										</Tag>
									</div>
								);
							})}

						{/* Photo Sync */}
						{asset.photoSync && asset.photoSync.id && (() => {
							const ps = asset.photoSync;
							const psOk = ps.syncStatus === "success";
							const psFailed = ps.syncStatus === "failed";
							const psRunning = ps.syncStatus === "running";
							const psSkipped = ps.syncStatus === "skipped";

							return (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 14,
										padding: "12px 16px",
										borderRadius: 8,
										border: `1px solid ${psOk ? "#b7eb8f" : psFailed ? "#ffa39e" : psRunning ? "#91caff" : "#d9d9d9"}`,
										background: psOk ? "#f6ffed" : psFailed ? "#fff2f0" : psRunning ? "#e6f4ff" : "#fafafa",
									}}
								>
									{/* Photo sync icon */}
									<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
										<rect width="40" height="40" rx="8" fill="#722ed1" />
										<text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="white" fontFamily="Arial, sans-serif" fontSize="18" fontWeight="bold">
											📷
										</text>
									</svg>

									{/* Info */}
									<div style={{ flex: 1, minWidth: 0 }}>
										<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
											<Text strong style={{ fontSize: 14 }}>Photo Sync</Text>
											<Tag color="blue" style={{ fontSize: 10, lineHeight: "18px", margin: 0 }}>
												Media
											</Tag>
										</div>
										<div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 4, flexWrap: "wrap" }}>
											<Text type="secondary" style={{ fontSize: 12 }}>
												<ClockCircleOutlined style={{ marginRight: 4 }} />
												{ps.lastSyncedAt
													? `Last synced ${new Date(ps.lastSyncedAt).toLocaleString()}`
													: "Never synced"}
											</Text>
											<Text style={{ fontSize: 12, color: "#52c41a" }}>
												✓ {ps.photosUploaded || 0} uploaded
											</Text>
											{(ps.photosSkipped || 0) > 0 && (
												<Text style={{ fontSize: 12, color: "#faad14" }}>
													⊘ {ps.photosSkipped} skipped
												</Text>
											)}
											{(ps.photosFailed || 0) > 0 && (
												<Text style={{ fontSize: 12, color: "#ff4d4f" }}>
													✕ {ps.photosFailed} failed
												</Text>
											)}
										</div>
									</div>

									{/* Status */}
									<Tag
										icon={psOk ? <CheckCircleOutlined /> : psFailed ? <CloseCircleOutlined /> : psRunning ? <ClockCircleOutlined /> : <ClockCircleOutlined />}
										color={psOk ? "success" : psFailed ? "error" : psRunning ? "processing" : psSkipped ? "warning" : "default"}
										style={{ fontSize: 12, padding: "2px 10px", margin: 0 }}
									>
										{ps.syncStatus?.toUpperCase() || "UNKNOWN"}
									</Tag>
								</div>
							);
						})()}
					</div>
				</Card>
			)}

			{/* Add Child Asset Modal */}
			<Modal
				title={
					<Space>
						<ApartmentOutlined />
						{`Add Child Asset under ${asset.assetTag || `Asset #${asset.id}`}`}
					</Space>
				}
				open={showAddChildModal}
				onCancel={() => setShowAddChildModal(false)}
				onOk={handleAddChild}
				okText="Create Child Asset"
				confirmLoading={addingChild}
				okButtonProps={{ disabled: !childForm.organizationAssetTypeId }}
				destroyOnClose
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
					<Alert
						type="info"
						showIcon
						message={
							<span style={{ fontSize: 13 }}>
								The child will be created at this asset's location with a
								virtual tag (e.g.{" "}
								<strong>{`${asset.assetTag || `AST-${asset.id}`}.${childAssets.length + 1}`}</strong>
								) unless you enter one below.
							</span>
						}
					/>

					<div>
						<Text strong style={{ fontSize: 13 }}>
							Asset Type <Text type="danger">*</Text>
						</Text>
						<Select
							style={{ width: "100%", marginTop: 4 }}
							placeholder="Select asset type"
							loading={loadingOptions}
							showSearch
							optionFilterProp="label"
							value={childForm.organizationAssetTypeId || undefined}
							onChange={(value) =>
								setChildForm((prev) => ({ ...prev, organizationAssetTypeId: value }))
							}
							options={assetTypes.map((type) => ({
								value: type.id,
								label: type.name,
							}))}
						/>
					</div>

					<div>
						<Text strong style={{ fontSize: 13 }}>Asset Tag</Text>
						<Input
							style={{ marginTop: 4 }}
							placeholder="Leave blank to auto-generate from parent tag"
							value={childForm.assetTag || ""}
							onChange={(e) =>
								setChildForm((prev) => ({ ...prev, assetTag: e.target.value }))
							}
						/>
					</div>

					<Row gutter={12}>
						<Col span={12}>
							<Text strong style={{ fontSize: 13 }}>Manufacturer</Text>
							<Select
								style={{ width: "100%", marginTop: 4 }}
								placeholder="Optional"
								loading={loadingOptions}
								showSearch
								allowClear
								optionFilterProp="label"
								value={childForm.manufacturerId}
								onChange={(value) =>
									setChildForm((prev) => ({ ...prev, manufacturerId: value }))
								}
								options={manufacturers.map((m) => ({
									value: m.id,
									label: m.name,
								}))}
							/>
						</Col>
						<Col span={12}>
							<Text strong style={{ fontSize: 13 }}>Model #</Text>
							<Input
								style={{ marginTop: 4 }}
								placeholder="Optional"
								value={childForm.modelNumber || ""}
								onChange={(e) =>
									setChildForm((prev) => ({ ...prev, modelNumber: e.target.value }))
								}
							/>
						</Col>
					</Row>

					<Row gutter={12}>
						<Col span={12}>
							<Text strong style={{ fontSize: 13 }}>Serial #</Text>
							<Input
								style={{ marginTop: 4 }}
								placeholder="Optional"
								value={childForm.serialNumber || ""}
								onChange={(e) =>
									setChildForm((prev) => ({ ...prev, serialNumber: e.target.value }))
								}
							/>
						</Col>
						<Col span={12}>
							<Text strong style={{ fontSize: 13 }}>Condition</Text>
							<Select
								style={{ width: "100%", marginTop: 4 }}
								placeholder="Optional"
								allowClear
								value={childForm.condition}
								onChange={(value) =>
									setChildForm((prev) => ({ ...prev, condition: value }))
								}
								options={["Excellent", "Good", "Fair", "Poor"].map((c) => ({
									value: c,
									label: c,
								}))}
							/>
						</Col>
					</Row>

					<div>
						<Text strong style={{ fontSize: 13 }}>Notes</Text>
						<Input.TextArea
							style={{ marginTop: 4 }}
							rows={2}
							placeholder="Optional"
							value={childForm.generalNotes || ""}
							onChange={(e) =>
								setChildForm((prev) => ({ ...prev, generalNotes: e.target.value }))
							}
						/>
					</div>
				</div>
			</Modal>

			{/* Move Asset Modal */}
			{asset && (
				<MoveAssetModal
					visible={showMoveModal}
					onClose={() => setShowMoveModal(false)}
					onSuccess={() => {
						setShowMoveModal(false);
						fetchAssetDetail();
					}}
					asset={{
						id: asset.id,
						assetTag: asset.assetTag,
						location: asset.location
							? {
									id: asset.location.id,
									name: asset.location.name || "",
									address1: asset.location.address1,
									city: asset.location.city,
									state: asset.location.state,
									storeNumber: (asset.location as any).storeNumber || (asset.location as any).store_number,
								}
							: undefined,
					}}
				/>
			)}
		</div>
	);
};

export { AdminAssetDetail };
export type { AdminAssetDetailProps, AssetAggregate };
